import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'react-toastify'
import { AxiosError } from 'axios'

import { axiosInstance } from '../helpers'
import {
    VoiceAgentDocument,
    VoiceAgentDocumentDetail,
} from '../models/VoiceAgent'
import { useUser } from './useUser'

const VOICE_AGENT_KEY = 'voice-agent'
const BASE = '/voice-agent'

const extractError = (error: unknown, fallback: string): string => {
    const data = (error as AxiosError)?.response?.data as
        | Record<string, unknown>
        | undefined

    if (data) {
        if (typeof data.error === 'string' && data.error) {
            return data.error
        }
    }

    return fallback
}

export const useVoiceAgentDocuments = () => {
    const queryClient = useQueryClient()
    const { workspaceUser } = useUser()

    const apartmentId = workspaceUser?.active_workspace?.id
    const isApartment =
        workspaceUser?.active_workspace?.company_type === 'APARTMENT'

    const invalidate = () => {
        queryClient.invalidateQueries({
            queryKey: [VOICE_AGENT_KEY, 'documents'],
        })
        queryClient.invalidateQueries({
            queryKey: [VOICE_AGENT_KEY, 'setup-data'],
        })
    }

    const { data: documents, isLoading, error, refetch } = useQuery<
        VoiceAgentDocument[],
        AxiosError
    >({
        queryKey: [VOICE_AGENT_KEY, 'documents', apartmentId],
        queryFn: async () => {
            const res = await axiosInstance.get<VoiceAgentDocument[]>(
                `${BASE}/setup/documents/`,
            )
            return res.data
        },
        enabled: isApartment && !!apartmentId,
        refetchOnWindowFocus: false,
    })

    const upload = useMutation<VoiceAgentDocumentDetail, AxiosError, File>({
        mutationFn: async (file) => {
            const formData = new FormData()
            formData.append('file', file)
            const res = await axiosInstance.post<VoiceAgentDocumentDetail>(
                `${BASE}/setup/documents/`,
                formData,
                { headers: { 'Content-Type': 'multipart/form-data' } },
            )
            return res.data
        },
        onSuccess: () => {
            invalidate()
            toast.success('Document uploaded')
        },
        onError: (err) => {
            toast.error(extractError(err, 'Failed to upload document'))
        },
    })

    const remove = useMutation<void, AxiosError, number>({
        mutationFn: async (id) => {
            await axiosInstance.delete(`${BASE}/setup/documents/${id}/`)
        },
        onSuccess: () => {
            invalidate()
            toast.success('Document deleted')
        },
        onError: (err) => {
            toast.error(extractError(err, 'Failed to delete document'))
        },
    })

    return {
        documents: documents ?? [],
        isLoading,
        error,
        refetch,
        upload,
        remove,
    }
}

export const fetchDocumentDetail = (id: number) =>
    axiosInstance
        .get<VoiceAgentDocumentDetail>(`${BASE}/setup/documents/${id}/`)
        .then((res) => res.data)

export const downloadDocumentFile = async (id: number, filename: string) => {
    const res = await axiosInstance.get<Blob>(
        `${BASE}/setup/documents/${id}/file/`,
        { responseType: 'blob' },
    )
    const url = URL.createObjectURL(res.data)
    const a = document.createElement('a')
    a.href = url
    a.download = filename
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
}
